import { Injectable } from '@angular/core';
import { IUserProfile } from '../models/user-profile.interface';
import { IUserBillingAddress } from '../models/user-billing-address.interface';
import { DocumentReference, Firestore, doc, getDoc, setDoc, updateDoc } from '@angular/fire/firestore';
import { ConfirmController } from '../controllers/confirm.controller';

@Injectable({
  providedIn: 'root'
})
export class ProfileService {

  userProfile!: IUserProfile;
  private userBillingAddress!: IUserBillingAddress;
  private userDocRef!: DocumentReference;

  get name() {
    return this.userProfile && this.userProfile.name || '';
  }

  get email() {
    return this.userProfile && this.userProfile.email || '';
  }
  get first_family_name() {
    return this.userProfile && this.userProfile.first_family_name || '';
  }
  get dob() {
    return this.userProfile && this.userProfile.dob || '';
  }
  get gender() {
    return this.userProfile && this.userProfile.gender || '';
  }
  get phone() {
    return this.userProfile && this.userProfile.phone || '';
  }
  get picture() {
    return this.userProfile && this.userProfile.picture || '';
  }

  get billing_razon_social() {
    return this.userBillingAddress && this.userBillingAddress.razon_social || '';
  }

  get billing_rfc() {
    return this.userBillingAddress && this.userBillingAddress.rfc || '';
  }

  get billing_regimen_fiscal() {
    return this.userBillingAddress && this.userBillingAddress.regimen_fiscal || '';
  }

  get billing_uso_cfdi() {
    return this.userBillingAddress && this.userBillingAddress.uso_cfdi || '';
  }

  get billing_email() {
    return this.userBillingAddress && this.userBillingAddress.email || '';
  }
  get billing_phone() {
    return this.userBillingAddress && this.userBillingAddress.phone || '';
  }
  get billing_address1() {
    return this.userBillingAddress && this.userBillingAddress.address1 || '';
  }
  get billing_exterior_number() {
    return this.userBillingAddress && this.userBillingAddress.exterior_number || '';
  }
  get billing_interior_number() {
    return this.userBillingAddress && this.userBillingAddress.interior_number || '';
  }
  get billing_zip() {
    return this.userBillingAddress && this.userBillingAddress.zip || '';
  }
  get billing_state() {
    return this.userBillingAddress && this.userBillingAddress.state || '';
  }
  get billing_county() {
    return this.userBillingAddress && this.userBillingAddress.county || '';
  }
  get billing_neighborhood() {
    return this.userBillingAddress && this.userBillingAddress.neighborhood || '';
  }

  constructor(
    private firestore: Firestore,
    private confirmController: ConfirmController
  ) { }

  async register(registrationId: string, registration: any) {

    try {
      const registrationData = {
        name: registration.name,
        first_family_name: registration.first_family_name,
        dob: registration.dob,
        agreement: registration.agreement,
        agreement_date: new Date(),
        email: registration.email,
        virtualCards: [registration.virtualCard]
      };

      await setDoc(doc(this.firestore, 'userProfile', registrationId), registrationData);

      return true;
    } catch (e) {
      console.log('Error registering profile: RP01', e);
    }

    return false;
  }

  async login(registrationId: string) {
    const firestore = this.firestore;

    try {
      this.userDocRef = doc(firestore, "userProfile", registrationId)
      const profileDoc = await getDoc(this.userDocRef);
      if (profileDoc.exists()) {
        this.userProfile = {
          name: profileDoc.get('name'),
          first_family_name: profileDoc.get('first_family_name'),
          dob: profileDoc.get('dob'),
          gender: profileDoc.get('gender'),
          phone: profileDoc.get('phone'),
          agreement: profileDoc.get('agreement'),
          agreement_date: profileDoc.get('agreement_date'),
          picture: profileDoc.get('picture'),
          email: profileDoc.get('email'),
        }

        this.userBillingAddress = profileDoc.get('billingAddress');
      } else {
        return false;
      }
    } catch (e) {
      return false
    }
    return true;
  }

  logout() {
    this.userProfile = {
      name: '',
      first_family_name: '',
      dob: '',
      gender: '',
      phone: '',
      agreement: false,
      agreement_date: new Date(0),
      picture: '',
      email: '',
    }
  }

  async updateData(profile: any) {
    const firestore = this.firestore;
    const userProfile = this.userProfile;

    try {
      const data: any = {
        name: profile.name,
        first_family_name: profile.first_family_name,
        dob: profile.dob,
        gender: profile.gender,
        phone: profile.phone,
      }
      if (profile.email) {
        // Add email to the object if it exists
        data['email'] = profile.email;
      }
      await updateDoc(doc(firestore, 'userProfile', this.userDocRef.id), data);

      // if Succesful database update, update local profile
      userProfile.name = profile.name;
      userProfile.first_family_name = profile.first_family_name;
      userProfile.dob = profile.dob;
      userProfile.gender = profile.gender;
      userProfile.phone = profile.phone;
      return true;
    } catch (e) {
      return false;
    }
  }

  async updatePicture(pictureDataUrl: string) {
    try {
      await updateDoc(doc(this.firestore, 'userProfile', this.userDocRef.id), {
        picture: pictureDataUrl,
      });

      // if Succesful database update, update local profile
      this.userProfile.picture = pictureDataUrl;
      return true;
    } catch (e: any) {
      if (e.name === 'FirebaseError') {
        await this.confirmController.createAndPresent({
          message: "Selecciona una imagen menor a 2MB",
        });
      }
      return false;
    }
  }

  async deletePicture() {
    try {
      await updateDoc(doc(this.firestore, 'userProfile', this.userDocRef.id), {
        picture: '',
      });
      this.userProfile.picture = '';

      return true;
    } catch (e) {
      return false;
    }
  }

  async updateFCMToken(toke: string) {
    try {
      await updateDoc(doc(this.firestore, 'userProfile', this.userDocRef.id), {
        fcm: toke,
      });
      return true;
    } catch (e) {
      return false;
    }
  }


  //para guardar las casas de hallowen
  async saveHallowenHouse(registrationId: string, house: any) {
    let data: any;
    house.visitedTime = new Date();
    try {
      this.userDocRef = doc(this.firestore, "userProfile", registrationId)
      let profileDoc = (await getDoc(this.userDocRef)).data();

      if (profileDoc) {
        let houses = profileDoc['hallowenHouses'];

        if (houses) {
          houses.push(house);
          profileDoc['hallowenHouses'] = houses;
        } else {
          profileDoc['hallowenHouses'] = [house];
        }
      }

      data = profileDoc;
      await updateDoc(doc(this.firestore, 'userProfile', this.userDocRef.id), data);

    } catch (e) {
      console.log("ERROR", e);
      return false
    }
    return data['hallowenHouses'];
  }

  //para guardar las pregunts de hallowen
  async saveHallowenQuestions(registrationId: string, questions: any) {
    let data: any;

    try {
      this.userDocRef = doc(this.firestore, "userProfile", registrationId)
      let profileDoc = (await getDoc(this.userDocRef)).data();

      if (profileDoc) {
        profileDoc['halloweenQuestions'] = questions;
      }

      data = profileDoc;
      await updateDoc(doc(this.firestore, 'userProfile', this.userDocRef.id), data);

    } catch (e) {
      console.log("ERROR", e);
      return false
    }
    return data['halloweenQuestions'];
  }

  //obtener las casas de hallowen
  async getHallowenHouses(registrationId: string) {

    try {
      this.userDocRef = doc(this.firestore, "userProfile", registrationId)
      let profileDoc = (await getDoc(this.userDocRef)).data();

      if (profileDoc) {
        //console.log("profileDoc['hallowenHouses'];", profileDoc['hallowenHouses']);

        return profileDoc['hallowenHouses'];
      }
    } catch (e) {
      console.log("ERROR", e);
      return false
    }
  }

  //obtener las casas de hallowen
  async getHallowenQuestions(registrationId: string) {
    try {
      this.userDocRef = doc(this.firestore, "userProfile", registrationId)
      let profileDoc = (await getDoc(this.userDocRef)).data();

      if (profileDoc) {

        //console.log("profileDoc['halloweenQuestions'];", profileDoc['halloweenQuestions']);
        return profileDoc['halloweenQuestions'];
        // return profileDoc;

      }
    } catch (e) {
      return {};
    }
  }




}
